import { useTranslation } from 'react-i18next';
import styles from './TracksBar.module.scss';
import AssetTrackCard from '../asset-track-card/AssetTrackCard';
import { CSSProperties, useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@casops/store/hooks';
import {
  clearTracks,
  MAX_ASSET_TRACKS,
  requestViewTracks,
} from '@casops/store/slices/assetTracksSlice';
import { VdsButton, VdsIcon } from '@volt/volt-extended-components-react';
import { openSidePanel, SidePanel } from '@casops/store/slices/globalSlice';
import { ModelName } from '@casops/store/api';

export const TracksBar = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { trackIds, tracks } = useAppSelector(state => state.assetTracks);

  const hasVisibleTracks = useMemo<boolean>(
    () => Object.values(tracks).some(({ visible }) => visible === true),
    [tracks],
  );

  // Cleanup assetTracks slice on unmount.
  useEffect(() => {
    return () => {
      dispatch(clearTracks());
    };
  }, [dispatch]);

  const onAddTracksClick = useCallback(() => {
    dispatch(openSidePanel({ sidePanel: SidePanel.AssetTracks }));
  }, [dispatch]);

  const onGoToTracksClick = useCallback(() => {
    dispatch(requestViewTracks());
  }, [dispatch]);

  const limitReached = trackIds.length >= MAX_ASSET_TRACKS;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.header__title}>
          {t('Model.Collection.Title', { modelName: ModelName.AssetTrack })}
        </div>
        <div className={styles.header__limit}>
          {limitReached ? t('Validation.AssetTrackLimitReached', { count: MAX_ASSET_TRACKS }) : ''}
        </div>
        <div className={styles.header__actions}>
          <VdsButton
            variant="ghost"
            size="medium"
            onClick={onAddTracksClick}
            disabled={limitReached}
            style={
              {
                '--color-text-brand': 'var(--color-link-default)',
              } as CSSProperties
            }
          >
            {t('AddAssetTracks')}
            <VdsIcon slot="icon-left" svgIcon="add" />
          </VdsButton>
          <VdsButton
            variant="ghost"
            size="medium"
            onClick={onGoToTracksClick}
            disabled={!hasVisibleTracks}
            style={
              {
                '--color-text-brand': 'var(--color-link-default)',
              } as CSSProperties
            }
          >
            {t('GoToTracks')}
            <VdsIcon slot="icon-left" svgIcon="zoom" />
          </VdsButton>
        </div>
      </header>
      {trackIds.length > 0 && (
        <div className={styles.tracksBar}>
          {trackIds.map(trackId => (
            <AssetTrackCard key={trackId} trackId={trackId} />
          ))}
        </div>
      )}
    </div>
  );
};

export default TracksBar;
